import { defaultLog, logError } from "./utils/logService";
import mapiConfig from "../configs/mapi";
import * as Sentry from "@sentry/browser";
import { ConfigurationDispatcher } from "../redux/dispatcher/configurationDispatcher";
import { actionTypesConfigurations } from "../redux/constants/actionTypesConfigurations";
// Type-only import: erased at compile time, so this creates no runtime
// require() and cannot form a circular dependency with usersService.ts.
import type { authClaimsModel } from "./usersService";

const BASE_URL = mapiConfig.api.domain;

// Session-scoped cache for /users/userinfo claims. Lives here (not in
// usersService.ts, and not in redux) so that usersService.ts — a
// widely-imported "hub" file — only ever needs a one-directional import
// of authService.ts, never the reverse. authService.ts importing
// usersService.ts at runtime would close a cycle through
// usersService -> httpService -> tokenService -> authService.
let cachedAuthClaims: authClaimsModel | null = null;

function getCachedAuthClaims(): authClaimsModel | null {
  return cachedAuthClaims;
}

function setCachedAuthClaims(claims: authClaimsModel): void {
  cachedAuthClaims = claims;
}

function clearCachedAuthClaims(): void {
  cachedAuthClaims = null;
}

async function getAuth(username: string, password: string) {
  try {
    const response = await fallbackAuth(username, password);
    return response;
  } catch (error) {
    Sentry.captureException(error);
    logError(`getAuth error: ${error}`);
    return { status: "FAILED", isLoading: false };
  }
}

async function forgotPassword(username: string) {
  try {
    const pathURL = BASE_URL.endsWith("/")
      ? `${BASE_URL}users/forgot-password`
      : `${BASE_URL}/users/forgot-password`;

    const response = await fetch(pathURL, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ username }),
    });

    if (!response.ok) {
      throw new Error(`Forgot password failed: ${response.statusText}`);
    }

    return { status: "ACTIVE" };
  } catch (error) {
    Sentry.captureException(error);
    logError(`forgotPassword error: ${error}`);
    return { status: "FAILED" };
  }
}

async function fallbackAuth(username: string, password: string) {
  try {
    const pathURL = BASE_URL.endsWith("/")
      ? `${BASE_URL}users/login`
      : `${BASE_URL}/users/login`;

    const response = await fetch(pathURL, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ username, password }),
    });

    if (!response.ok) {
      throw new Error(`Fallback authentication failed: ${response.statusText}`);
    }

    const data = await response.json();
    if (data.accessToken && data.refreshToken) {
      const { accessToken, refreshToken, expiresAt, user } = data;
      setCustomTokens(accessToken, refreshToken, expiresAt, user);

      // Deliberately NOT dispatching IsUserAuthorized here. RootTabNavigator's
      // bootstrap effect (guarded by `if (!isUserAuthorized)`) is the single
      // place that flips this flag *and* kicks off InitUser() together. If we
      // set it here too, RootTabNavigator mounts post-login with
      // isUserAuthorized already true, its guard short-circuits, and
      // InitUser()/getUserInfo()/getUsers()/notifications never run.
      return {
        status: "ACTIVE",
        isLoading: false,
        user: data.user,
        token: accessToken,
      };
    }

    return {
      status: "FAILED",
      isLoading: false,
    };
  } catch (error) {
    Sentry.captureException(error);
    logError(`Fallback auth error: ${error}`);
    return {
      status: "FAILED",
      isLoading: false,
    };
  }
}

function setCustomTokens(
  accessToken: string,
  refreshToken: string,
  expiresAt: any,
  user: any,
) {
  localStorage.setItem("accessToken", accessToken);
  localStorage.setItem("refreshToken", refreshToken);
  localStorage.setItem("expiresAt", expiresAt);
}

function getCustomAccessToken() {
  const accessToken = localStorage.getItem("accessToken");
  const expiresAt = new Date(localStorage.getItem("expiresAt") || "0");

  if (Date.now() > expiresAt.getTime()) {
    return null; // Token expired
  }

  return accessToken;
}

function getRefreshToken() {
  return localStorage.getItem("refreshToken");
}

function clearTokens() {
  localStorage.removeItem("accessToken");
  localStorage.removeItem("refreshToken");
  localStorage.removeItem("expiresAt");
  localStorage.removeItem("sub");
}

async function getCustomSession() {
  const accessToken = getCustomAccessToken();
  if (!accessToken) {
    return null; // No active session
  }

  return { accessToken };
}

async function customLogout() {
  const accessToken = getCustomAccessToken();

  try {
    if (accessToken) {
      await fetch(`${BASE_URL}auth/logout`, {
        method: "POST",
        headers: { Authorization: `Bearer ${accessToken}` },
      });
    }
  } catch (error) {
    logError(`Logout error: ${error}`);
  } finally {
    clearTokens();
    clearCachedAuthClaims();
    ConfigurationDispatcher(actionTypesConfigurations.IsUserAuthorized, false);
  }
}

export {
  forgotPassword,
  getCustomSession,
  getCustomAccessToken,
  getAuth,
  getRefreshToken,
  customLogout,
  getCachedAuthClaims,
  setCachedAuthClaims,
  clearCachedAuthClaims,
};
